import * as amplitude from "@amplitude/analytics-react-native"
import { DialogM } from "@common/components/alert/dialog/Dialog"
import {
    SUPPORT_REPLY_TARGET,
    supportReplyTarget,
} from "@common/components/chat/supportChatAccess"
import {
    buildDeepLinkUrl,
    isAuthOnlyPath,
    isParentPath,
    needsModeSwitch,
    resolveMenuScreenName,
} from "@common/utils/navigationUtils"
import { apiIdle, apiRequest } from "@data/redux/actions/api"
import { APP_URL } from "@data/constants/apiConstants"
import { setPendingIntercomOpen } from "@data/redux/actions/intercom"
import { setPendingDeepLink } from "@data/redux/actions/notifications"
import { authService } from "@data/services/authService"
import { useNavigation } from "@react-navigation/native"
import Constants from "expo-constants"
import * as Device from "expo-device"
import * as Notifications from "expo-notifications"
import moment from "moment"
import React, { useEffect } from "react"
import { Platform } from "react-native"
import { connect } from "react-redux"
import { setIsLiveModeEnabled } from "../../data/redux/actions/liveMode"
import {
    clearLastNotificationIdentifier,
    setLastNotificationIdentifier,
} from "../../data/redux/actions/notifications"
import { hasRole } from "../../data/utility/ability"
import { studentHomePushPath } from "../../ports/primary/ui/screens/student/studentPushTargets"
import { theme } from "../theme/theme"

const NotificationSubscriptionHandler = (props) => {
    const navigation = useNavigation()

    // ** Manage push notification responses
    const lastNotificationResponse =
        Platform.OS !== "web"
            ? Notifications.useLastNotificationResponse()
            : null

    React.useEffect(() => {
        if (Platform.OS !== "web")
            if (
                lastNotificationResponse &&
                lastNotificationResponse.actionIdentifier ===
                    Notifications.DEFAULT_ACTION_IDENTIFIER &&
                lastNotificationResponse.notification.request.identifier !==
                    props.lastNotificationIdentifier
            ) {
                // save identifier in order not to process the notification twice
                props.setLastNotificationIdentifier(
                    lastNotificationResponse.notification.request.identifier
                )

                // track in amplitude
                amplitude.track(
                    "push.notif.clicked" +
                        (lastNotificationResponse.notification.request.content
                            .data?.path
                            ? `.${lastNotificationResponse.notification.request.content.data?.path}`
                            : "")
                )

                if (
                    lastNotificationResponse.notification.request.content.data
                        .path
                ) {
                    const path =
                        lastNotificationResponse.notification.request.content
                            .data.path
                    const activityId =
                        lastNotificationResponse.notification.request.content
                            .data.activity_id
                    const substitutionProposalId =
                        lastNotificationResponse.notification.request.content
                            .data.substitution_proposal_id
                    const groupAttributionProposalId =
                        lastNotificationResponse.notification.request.content
                            .data.group_attribution_proposal_id
                    const messageData =
                        lastNotificationResponse.notification.request.content
                            .data.data
                    const conversationId =
                        lastNotificationResponse.notification.request.content
                            .data.conversation_id

                    switch (path) {
                        case "activities-for-review":
                            if (activityId)
                                navigation.push("activity-review", {
                                    activityId: activityId,
                                })
                            return
                        case "challenges":
                            return
                        case "new-review":
                            return
                        case "coaching":
                            navigation.navigate("menu", { screen: "coaching" })
                            return
                        // The singular path (group offer push, targeted-offer reminder) has no
                        // screen of its own: it opens the same one.
                        case "group-attribution-proposal":
                        case "group-attribution-proposals":
                            // Reminder notification (no ID) -> go to proposals list
                            // Single proposal notification (with ID) -> go to single proposal screen
                            if (groupAttributionProposalId) {
                                navigation.push("group-attribution-proposals", {
                                    group_attribution_proposal_id:
                                        groupAttributionProposalId,
                                })
                            } else {
                                navigation.navigate("menu", {
                                    screen: "proposals",
                                })
                            }
                            return

                        case "live-session":
                            // if live mode not enabled, enable it with the right live session id
                            if (!props.isLiveMode) {
                                navigation.navigate("menu", {
                                    screen: "coaching",
                                })
                            }

                            return
                        case "substitution-proposals":
                            if (substitutionProposalId)
                                navigation.push("substitution-proposals", {
                                    substitution_proposal_id:
                                        substitutionProposalId,
                                })
                            return
                        case "messages":
                            // Live thread message
                            if (
                                messageData?.meta?.thread_type_verbose ===
                                "GROUP"
                            ) {
                                const threadLiveSession =
                                    props.user?.live_sessions?.find((s) => {
                                        const liveMoment = moment(
                                            s?.day_time,
                                            "dddd HH:mm",
                                            "en"
                                        ).locale("fr")
                                        const isToday = liveMoment?.isSame(
                                            moment(),
                                            "day"
                                        )
                                        if (
                                            s?.thread_id?.toString() ===
                                                messageData?.thread_id?.toString() &&
                                            isToday
                                        )
                                            return s
                                        return null
                                    })
                                // if live mode not enabled, enable it with the right live session id
                                if (!props.isLiveMode) {
                                    if (threadLiveSession)
                                        navigation.push("pre-call", {
                                            liveSessionId:
                                                threadLiveSession?.id,
                                        })
                                    else
                                        DialogM.show({
                                            text1: "La conversation n'a pas été trouvée",
                                        })
                                }
                                // if live mode enabled
                                else {
                                    // if good live id, navigate to live-messages screen
                                    if (
                                        props.currentLiveSession.thread_id ===
                                        messageData?.thread_id
                                    ) {
                                        navigation.navigate("messages", {
                                            userId: 0,
                                        })
                                    }
                                    // if wrong live id
                                    else {
                                        DialogM.show({
                                            text1: "Message reçu dans une autre session",
                                            text2: "Pour y accéder, il faut quitter cette session live et ouvrir l'autre",
                                        })
                                    }
                                }
                            } else {
                                // if live mode not enabled, enable it with the right live session id
                                if (!props.isLiveMode) {
                                    if (hasRole(props.user, "student")) {
                                        navigation.navigate("messenger", {
                                            tutorId: messageData?.owner_id,
                                        })
                                    } else if (
                                        props.user.students?.find(
                                            (st) =>
                                                st?.id === messageData?.owner_id
                                        )
                                    ) {
                                        navigation.navigate("messenger", {
                                            studentId:
                                                props.user?.students?.find(
                                                    (st) =>
                                                        st?.id ===
                                                        messageData?.owner_id
                                                )?.id,
                                        })
                                    } else {
                                        DialogM.show({
                                            text1: "La conversation n'a pas été trouvée",
                                        })
                                    }
                                }
                                // if live mode enabled
                                else {
                                    const thread =
                                        props.currentLiveSession.thread_ids[
                                            messageData?.owner_id
                                        ]

                                    // if good live id, navigate to live-messages screen
                                    if (thread) {
                                        navigation.navigate("messages", {
                                            userId: messageData.owner_id,
                                            //userId: 124
                                        })
                                    }
                                    // if wrong live id
                                    else {
                                        DialogM.show({
                                            text1: "Message reçu dans une autre session",
                                            text2: "Pour y accéder, il faut quitter cette session live",
                                        })
                                    }
                                }
                            }
                            return

                        case "incoming-call":
                            const threadLiveSession2 =
                                props.user?.live_sessions?.find(
                                    (s) =>
                                        s?.id === messageData?.live_session_id
                                )

                            // if live mode not enabled, enable it with the right live session id
                            if (!props.isLiveMode) {
                                if (threadLiveSession2) {
                                    navigation.push("pre-call", {
                                        liveSessionId: threadLiveSession2?.id,
                                    })
                                }
                            }
                            // if live mode enabled
                            else {
                                // if good live id, nothing to do
                                if (
                                    props.liveSessionId ===
                                    threadLiveSession2?.id
                                ) {
                                }
                                // if wrong live id
                                else {
                                    DialogM.show({
                                        text1: "Appel reçu dans une autre session",
                                        text2: "Pour y accéder, il faut quitter cette session et ouvrir l'autre",
                                    })
                                }
                            }

                            return

                        case "support-chat": {
                            // Réponse du support : IntercomChat ouvre la
                            // conversation dès que sa session identifiée est
                            // prête. Le drapeau porte la conversation pour
                            // atterrir sur le fil, pas sur la boîte de réception.
                            const target = supportReplyTarget(
                                props.user,
                                props.isParentMode
                            )
                            if (!target) return
                            props.setPendingIntercomOpen(true, conversationId)
                            if (target === SUPPORT_REPLY_TARGET.PARENT_MODE) {
                                props.setPendingDeepLink(
                                    buildDeepLinkUrl(APP_URL, "menu/parent")
                                )
                            }
                            return
                        }

                        default:
                            // Short-form "home" is role- and mode-specific:
                            // tutors → menu/dashboard, parent-mode users →
                            // menu/parent (avoids a pointless mode switch on
                            // shared devices), students → menu/home, or
                            // menu/activities in the design-system student
                            // space, whose home no longer lists the activities
                            // (`studentHomePushPath`). Normalize here so the
                            // rest of the default branch (auth / mode / menu
                            // resolution) sees a full-form path.
                            const resolvedPath =
                                path === "home"
                                    ? hasRole(props.user, "tutor") ||
                                      hasRole(props.user, "tutor-candidate")
                                        ? "menu/dashboard"
                                        : props.isParentMode
                                          ? "menu/parent"
                                          : props.studentHomePushPath
                                    : path

                            // User is logged in (push notifications require auth) — auth-only
                            // screens (login, register, etc.) don't apply, so discard.
                            if (isAuthOnlyPath(resolvedPath)) return

                            // If a mode switch is needed, defer the whole transition
                            // (mode flip + family-id resolution + navigation) to
                            // RootNavigator via pendingDeepLink — it owns the full flow.
                            // Encode primitive messageData onto the URL so the target
                            // screen still receives its params after the mode flip.
                            if (
                                needsModeSwitch(
                                    resolvedPath,
                                    props.isParentMode
                                )
                            ) {
                                // Parent mode is a student-account toggle, not
                                // a separate role. Tutor accounts that receive
                                // a parent path (cross-account share, misroute)
                                // would land in empty parent UI — drop instead.
                                if (
                                    isParentPath(resolvedPath) &&
                                    (hasRole(props.user, "tutor") ||
                                        hasRole(props.user, "tutor-candidate"))
                                ) {
                                    return
                                }
                                props.setPendingDeepLink(
                                    buildDeepLinkUrl(
                                        APP_URL,
                                        resolvedPath,
                                        messageData
                                    )
                                )
                                return
                            }

                            // No mode switch needed — navigate directly
                            const menuScreen =
                                resolveMenuScreenName(resolvedPath)
                            if (menuScreen) {
                                navigation.navigate("menu", {
                                    screen: menuScreen,
                                    params: messageData,
                                })
                            } else {
                                navigation.navigate(resolvedPath, messageData)
                            }
                            return
                    }
                }
            }
    }, [lastNotificationResponse])

    const registerForPushNotificationsAsync = async () => {
        if (Device.isDevice) {
            const { status: existingStatus } =
                await Notifications.getPermissionsAsync()
            let finalStatus = existingStatus
            if (existingStatus !== "granted") {
                const { status } = await Notifications.requestPermissionsAsync()
                finalStatus = status
            }
            if (finalStatus !== "granted") {
                return "not_granted"
            }

            const projectId = Constants.expoConfig?.extra?.eas?.projectId
            const token = (
                await Notifications.getExpoPushTokenAsync({ projectId })
            ).data

            return token
        } else {
            console.log(
                "Vous devez utiliser un appareil physique pour les notifications push."
            )
        }

        if (Platform.OS === "android") {
            await Notifications.setNotificationChannelAsync("default", {
                name: "default",
                importance: Notifications.AndroidImportance.MAX,
                vibrationPattern: [0, 250, 250, 250],
                lightColor: theme.colors.primary,
            })
        }
    }

    useEffect(() => {
        // Register in parent mode too: the support-reply push is parent-facing,
        // and the token is keyed to the (student) account regardless of mode.
        if (!props.expoToken) {
            registerForPushNotificationsAsync().then((expo_token) => {
                if (expo_token === "not_granted" || !Device.isDevice) {
                    return
                }

                if (expo_token) {
                    props.apiRequest(
                        authService.subscribeToNotifications,
                        {},
                        { expo_token }
                    )
                } else {
                    DialogM.show({
                        text1: "Échec",
                        text2: "Impossible d'enregistrer l'appareil pour les notifications push",
                    })
                }
            })
        }
    }, [])

    useEffect(() => {
        // If any error then alert with the error and IDLE reset request state
        if (props.subscribeToNotificationsSuccess) {
            props.apiIdle(authService.subscribeToNotifications)
        } else if (props.subscribeToNotificationsError) {
            props.apiIdle(authService.subscribeToNotifications)
            DialogM.show({
                text1: "Erreur",
                text2: props.subscribeToNotificationsError?.message,
            })
        }
    }, [
        props.subscribeToNotificationsSuccess,
        props.subscribeToNotificationsError,
    ])

    return null
}

const mapStateToProps = (state) => {
    return {
        subscribeToNotificationsRequest:
            state.api.subscribeToNotifications?.request,
        subscribeToNotificationsSuccess:
            state.api.subscribeToNotifications?.success,
        subscribeToNotificationsError:
            state.api.subscribeToNotifications?.error,
        expoToken: state.data.session?.object?.expo_token,
        lastNotificationIdentifier: state.notifications.lastIdentifier,
        getStaticDataError: state.api.getStaticData?.error,
        getUserError: state.api.getUser?.error,

        isLiveMode: state.liveMode?.isLiveModeEnabled,
        liveSessionId: state.liveMode?.liveSessionId,
        liveSessions: state.data.currentUser?.object?.live_sessions,
        currentLiveSession: state.data.currentUser?.object?.live_sessions?.find(
            (x) => x?.id === state.liveMode?.liveSessionId
        ),
        user: state.data.currentUser?.object,
        activityTemplates: state.data.staticData?.activity_templates,
        token: state.data.session?.object?.token,
        isParentMode: state.data.family?.isParentMode,
        studentHomePushPath: studentHomePushPath(state),
    }
}

const mapDispatchToProps = {
    apiRequest,
    apiIdle,
    setLastNotificationIdentifier,
    clearLastNotificationIdentifier,
    setIsLiveModeEnabled,
    setPendingDeepLink,
    setPendingIntercomOpen,
}

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(NotificationSubscriptionHandler)
